import React, { useEffect, useRef, useState } from 'react';
import { Paper, Grid } from '@material-ui/core';
import CustomTable from 'components/CustomTable';
import TextHeader from 'components/CustomText/TextHeader';
import CustomTextField from 'components/Input/CustomTextField';
import styled from 'styled-components';
import NumberListFormItem from './NumberListFormItem';
import CustomButton from 'components/CustomInput/Button';
import useStudyArticleDetailData from 'hooks/tableData/useStudyArticleDetailData';
import useLicenceRegistrationData from 'hooks/tableData/useLicenceRegistrationData';
import { initializeTableData, produce, isHighlight as isHL } from 'utility/general';
import CustomOutlineSelect from 'components/Input/CustomOutlineSelect';
import DataListener from './DataListener';
import { useDispatch, useSelector } from 'react-redux';
import StudyDetailForProspectiveStudySiteInfo from './SiteInfo/StudyDetailForProspectiveStudySiteInfo';
import TableDialog from './TableDialog';
import { SET_STUDY_VALIDATION, UPDATE_FORM_VALIDITY } from 'store/key';
import { getStore } from 'store';
import { p2PhaseStudyList } from 'constants/globalEnum';

const SPaperLayout = styled.div`
    padding: 10px;
`;

const SLayout = styled.div`
    padding: 10px;
`;

export default function StudyDetailForProspectiveStudyFormLayout({
    tabType,
    defaultData = {},
    formId,
    applicationDetail = {},
    disableOnPermission = false,
    disableTableOperations = false,
    siteInfoId,
    onFormUpdate = (key, value) => null,
    onSiteInfoUpdate = (siteInfoId, key, value) => null,
    isAmendmentCreate = false,
    isAmendmentEdit = false,
    applicationCreateDtm,
    allComments,
    getCommentDetail,
    isEnableComment,
}) {
    const [postData, setPostData] = useState({});
    const [sitePostData, setSitePostData] = useState({});
    const studyDesignValue = useSelector(
        (state) => state.applicationDetailPostData?.postData?.applicationForm?.p2StudyDesign,
    );
    const dispatch = useDispatch();
    const [isInitialize, setInitialize] = useState(false);
    const isMount = useRef(true);
    const isAmendment = isAmendmentCreate || isAmendmentEdit;

    const [studyInfoValidation, setStudyInfoValidation] = useState({});

    const {
        openPopup: openStudyArticlePopup,
        setOpenPopup: setStudyArticleOpenPopup,
        columns: studyArticleColumns,
        rows: studyArticleRows,
        setRows: setStudyArticleRows,
        defaultFormList: defaultStudyArticleFormList,
        // selectedRow: selectedStudyArticleRow,
        handleRowCreate: handleStudyArticleRowCreate,
        onRecordUpdate: handleStudyArticleRecordUpdate,
        handleEditClick: handleStudyArticleEditClick,
        handleDelete: handleStudyArticleDelete,
        onEditSubmit: onStudyArticleEditSubmit,
        onDataEmpty: onStudyArticleDataEmpty,
        recoverDataCreation: recoverStudyArticleDataCreation,
        isEdit: isStudyArticleEdit,
        updateRemoteData: updateStudyArticleRemoteData,
    } = useStudyArticleDetailData({ formId });

    // console.log('122024 StudyDetailForProspectiveStudyFormLayout.jsx studyA', studyArticleRows);

    const {
        openPopup: openLicencePopup,
        setOpenPopup: setLicenceOpenPopup,
        columns: licenceColumns,
        rows: licenceRows,
        setRows: setLicenceRows,
        defaultFormList: defaultLicenceFormList,
        // handleRowCreate: handleLicenceRowCreate,
        onRecordUpdate: handleLicenceRecordUpdate,
        handleEditClick: handleLicenceEditClick,
        handleDelete: handleLicenceDelete,
        onEditSubmit: onLicenceEditSubmit,
        onDataEmpty: onLicenceDataEmpty,
        recoverDataCreation: recoverLicenceDataCreation,
        isEdit: isLicenceEdit,
        updateRemoteData: updateLicenceRemoteData,
    } = useLicenceRegistrationData({ formId });

    // when studyInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_STUDY_VALIDATION,
            payload: {
                [validationKey]: studyInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    // initialize the study-info postData if edit data exist
    useEffect(() => {
        if (tabType === 'info') {
            const { applicationDetailPostData } = getStore().getState();
            const postData = applicationDetailPostData.postData?.applicationForm ?? {};
            setPostData(postData);

            const studyArticleDetailData = initializeTableData({
                tableKey: 'studyArticleDetail',
                currentTableData: studyArticleRows,
            });

            setStudyArticleRows(studyArticleDetailData);

            const licenceData = initializeTableData({
                tableKey: 'studyArticleLicense',
                currentTableData: licenceRows,
            });

            setLicenceRows(licenceData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // initialize the site postData if edit data exist
    useEffect(() => {
        if (tabType === 'hospital') {
            const { applicationDetailPostData } = getStore().getState();
            const siteInfo = applicationDetailPostData.postData.siteInfo;
            const currentSiteInfoPostData = siteInfo?.find((item) => item.id === siteInfoId);
            setSitePostData(currentSiteInfoPostData ? { [siteInfoId]: currentSiteInfoPostData } : {});
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateStudyValidation = (name, isValidate) => {
        setStudyInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    // setting isInitialize after 1000ms
    useEffect(() => {
        setTimeout(() => {
            if (isMount.current) {
                setInitialize(true);
            }
        }, 1000);

        return () => {
            isMount.current = false;
        };
    }, []);

    const validationKey = 'studyDetailsForProspectiveStudy';

    const isStudyValid = useSelector(
        (state) => state.applicationDetailValid.isApplicationFormDetailValid?.[validationKey],
    );

    // update study-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(studyInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isStudyValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'study',
                    applicationFormId: formId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    const getData = (key) => {
        return postData[key] ?? defaultData[key];
    };

    const onFieldChange = (e) => {
        const { name, value } = e.target;

        setPostData((prevData) => {
            return {
                ...prevData,
                [name]: value,
            };
        });

        onFormUpdate(name, value);
    };

    const getDefaultData = (key) => {
        const site = applicationDetail.siteInfo.find((item) => {
            return item.id === siteInfoId;
        });

        return site?.[key];
    };

    const getSiteData = (key) => {
        return sitePostData?.[siteInfoId]?.[key] ?? getDefaultData(key);
    };

    const onSiteFieldChange = (e) => {
        const { value, name } = e.target;

        setSitePostData((prevData) => {
            const newData = produce(prevData, (draft) => {
                if (!draft[siteInfoId]) {
                    draft[siteInfoId] = {};
                }

                draft[siteInfoId][name] = value;
            });

            return newData;
        });

        onSiteInfoUpdate(siteInfoId, name, value);
    };

    const isProspective = () => {
        const temp = studyDesignValue ?? applicationDetail?.applicationForm?.p2StudyDesign;

        if (temp === 'P' || temp === 'B') {
            return true;
        }

        return false;
    };

    // keep update the validation of study article licence table
    useEffect(() => {
        let isValid = isLicenceTableValid(licenceRows);

        if (licenceRows.length === 0) {
            isValid = false;
        }

        if (!isProspective() || getData('p2StudyArticle') === false) {
            isValid = true;
        }

        updateStudyValidation('StudyArticleLicense', isValid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [licenceRows]);

    const isLicenceTableValid = (licenceRows) => {
        let isLicenceRowValid = true;

        // check the table record is complete
        licenceRows.forEach((item) => {
            if (item.sameHK === undefined) {
                isLicenceRowValid = false;
            }
        });

        return isLicenceRowValid;
    };

    const isHighlight = (keys, istable = false) => {
        return isHL(applicationDetail, siteInfoId, formId, tabType, keys, istable);
    };

    return (
        <SPaperLayout>
            <DataListener
                onPostDataReset={() => {
                    setSitePostData({});
                    setPostData({});
                    updateStudyArticleRemoteData();
                    updateLicenceRemoteData();
                }}
            />

            {openStudyArticlePopup && (
                <TableDialog
                    defaultFormList={defaultStudyArticleFormList}
                    title={'Study article details'}
                    onSubmit={async () => {
                        if (isStudyArticleEdit) {
                            const { index, returnData } = onStudyArticleEditSubmit();
                            const { articleControl, type, name } = returnData;


                            
                            onLicenceEditSubmit(index, { articleControl, type, name });
                        } else {
                            const dataToCreate = await handleStudyArticleRecordUpdate();
                            const { name, type, articleControl, id } = dataToCreate;
                            handleLicenceRecordUpdate({
                                name,
                                type,
                                articleControl,
                                id,
                            });
                        }
                    }}
                    onClose={() => setStudyArticleOpenPopup(false)}
                />
            )}

            {openLicencePopup && (
                <TableDialog
                    defaultFormList={defaultLicenceFormList}
                    title={'Study article licence registration'}
                    onSubmit={() => {
                        if (isLicenceEdit) {
                            onLicenceEditSubmit();
                        } else {
                            handleLicenceRecordUpdate();
                        }
                    }}
                    onClose={() => setLicenceOpenPopup(false)}
                />
            )}

            <Paper>
                <SLayout>
                    <TextHeader underline italic>
                        PART IIB: STUDY DETAILS FOR PROSPECTIVE STUDY
                    </TextHeader>
                    <NumberListFormItem bold number={'11'} label={'Study Article and Arrangements'} fontSize={16} />
                    {tabType === 'hospital' ? (
                        <StudyDetailForProspectiveStudySiteInfo
                            disableOnPermission={disableOnPermission}
                            formId={formId}
                            getSiteData={getSiteData}
                            siteInfoId={siteInfoId}
                            onSiteFieldChange={onSiteFieldChange}
                            applicationDetail={applicationDetail}
                            isProspective={isProspective()}
                            isHighlight={isHighlight}
                        />
                    ) : (
                        <>
                            <NumberListFormItem
                                number={'11.1'}
                                label={'How does the procedure/treatment differ from current treatment practice?'}
                                displayContentNextLine
                                highlight={isHighlight(['p2Procedure'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.1 How does the procedure/treatment differ from current treatment practice?', 
                                    commentField: 'p2Procedure',
                                    commentDetail: getCommentDetail("p2Procedure", allComments),
                                }}
                            >
                                <CustomOutlineSelect
                                    name={'p2Procedure'}
                                    value={getData('p2Procedure')}
                                    onAppFieldValidate={updateStudyValidation}
                                    disableOnPermission={disableOnPermission}
                                    disabled={!isProspective()}
                                    updateApplicationFormOnDisableChange={formId}
                                    onChange={onFieldChange}
                                    choices={[
                                        {
                                            key: 0,
                                            display:
                                                'Long established practice already adopted as one of the routine practices recently',
                                        },
                                        {
                                            key: 1,
                                            display:
                                                'Practice not widely used but already adopted in some advanced centres',
                                        },
                                        {
                                            key: 2,
                                            display:
                                                'Practice not widely used but benefits suggested in some preliminary publications',
                                        },
                                        { key: 3, display: 'Purely experimental' },
                                        { key: 4, display: 'Others' },
                                    ]}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.1.1'}
                                level={2}
                                highlight={isHighlight(['p2ProcedureOther'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.1.1 If others, specify', 
                                    commentField: 'p2ProcedureOther',
                                    commentDetail: getCommentDetail("p2ProcedureOther", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p2ProcedureOther')}
                                    name={'p2ProcedureOther'}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={255}
                                    allowAction={getData('p2Procedure') === 4}
                                    disabled={!isProspective()}
                                    onChange={onFieldChange}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    label={'If others, specify'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.1.2'}
                                label={'Methods of assignment'}
                                level={2}
                                highlight={isHighlight(['p2MethodAssignment'])}
                                labelWidth={150}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.1.2 Methods of assignment', 
                                    commentField: 'p2MethodAssignment',
                                    commentDetail: getCommentDetail("p2MethodAssignment", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15, display: 'flex' }}>
                                    <CustomOutlineSelect
                                        // style={{ width: 300 }}
                                        name={'p2MethodAssignment'}
                                        value={getData('p2MethodAssignment')}
                                        disabled={!isProspective()}
                                        disableOnPermission={disableOnPermission}
                                        onChange={onFieldChange}
                                        updateApplicationFormOnDisableChange={formId}
                                        onAppFieldValidate={updateStudyValidation}
                                        choices={[
                                            {
                                                key: 0,
                                                display: 'Randomization',
                                            },
                                            {
                                                key: 1,
                                                display: 'Assigned by the investigator but not randomized',
                                            },
                                            {
                                                key: 2,
                                                display: 'Observed assignment (cohort or case-control study)',
                                            },
                                            { key: 3, display: 'N/A' },
                                        ]}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.1.3'}
                                level={2}
                                label={'Degree of masking'}
                                labelWidth={150}
                                highlight={isHighlight(['p2DegreeMasking'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.1.3 Degree of masking', 
                                    commentField: 'p2DegreeMasking',
                                    commentDetail: getCommentDetail("p2DegreeMasking", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15, display: 'flex' }}>
                                    <CustomOutlineSelect
                                        // style={{ width: 300 }}
                                        name={'p2DegreeMasking'}
                                        value={getData('p2DegreeMasking')}
                                        disabled={!isProspective()}
                                        disableOnPermission={disableOnPermission}
                                        onChange={onFieldChange}
                                        onAppFieldValidate={updateStudyValidation}
                                        updateApplicationFormOnDisableChange={formId}
                                        choices={[
                                            {
                                                key: 0,
                                                display: 'Single-blinded',
                                            },
                                            {
                                                key: 1,
                                                display: 'Double-blinded',
                                            },
                                            {
                                                key: 2,
                                                display: 'Open-labeled',
                                            },
                                            { key: 3, display: 'N/A' },
                                        ]}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.1.4'}
                                level={2}
                                labelWidth={150}
                                labelTooltip={
                                    'Phase 1 Panels are available in HKU/HKW IRB and the Joint CUHK-NTEC CREC.'
                                }
                                label={'Phase of study'}
                                highlight={isHighlight(['p2PhaseStudy'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.1.4 Phase of study', 
                                    commentField: 'p2PhaseStudy',
                                    commentDetail: getCommentDetail("p2PhaseStudy", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15, display: 'flex' }}>
                                    <CustomOutlineSelect
                                        style={{ width: 200 }}
                                        name={'p2PhaseStudy'}
                                        disabled={!isProspective()}
                                        disableOnPermission={disableOnPermission}
                                        value={getData('p2PhaseStudy')}
                                        onAppFieldValidate={updateStudyValidation}
                                        updateApplicationFormOnDisableChange={formId}
                                        onChange={onFieldChange}
                                        choices={p2PhaseStudyList}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.2'}
                                label={'Study article'}
                                labelTooltip={
                                    'E.g. chemical drugs, biological drugs, herbal medicines and medical devices, etc.'
                                }
                            />
                            <NumberListFormItem
                                number={'11.2.1'}
                                showTrueFalse
                                level={2}
                                label={'Is there any study article?'}
                                trueFalseValue={getData('p2StudyArticle')}
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                name={'p2StudyArticle'}
                                updateApplicationFormOnDisableChange={formId}
                                disableCheckBox={!isProspective()}
                                onTrueFalseSelected={(e) => {
                                    onFieldChange(e);
                                    if (e.target.value) {
                                        recoverStudyArticleDataCreation();
                                        recoverLicenceDataCreation();
                                    } else {
                                        onStudyArticleDataEmpty();
                                        onLicenceDataEmpty();
                                    }
                                }}
                                highlight={isHighlight(['p2StudyArticle'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.2.1 Is there any study article?', 
                                    commentField: 'p2StudyArticle',
                                    commentDetail: getCommentDetail("p2StudyArticle", allComments),
                                }}
                            />
                            <NumberListFormItem
                                displayContentNextLine
                                number={'11.2.2'}
                                level={2}
                                label={'Study article details'}
                                highlight={isHighlight(['studyArticleDetail'], true)}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.2.2 Study article details', 
                                    commentStatus: null,
                                    commentField: 'studyArticleDetail',
                                    commentDetail: getCommentDetail("studyArticleDetail", allComments),
                                }}
                            >
                                <Grid container>
                                    <CustomButton
                                        style={{ marginBottom: 8 }}
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        allowAction={getData('p2StudyArticle') === true}
                                        disabled={!isProspective()}
                                        onClick={handleStudyArticleRowCreate}
                                    >
                                        Add
                                    </CustomButton>
                                    <CustomTable
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        rows={studyArticleRows}
                                        columns={studyArticleColumns}
                                        isEditAllow
                                        isDeleteAllow
                                        disableOldDataDelete={isAmendment}
                                        allowAction={getData('p2StudyArticle') === true}
                                        disabled={!isProspective()}
                                        name={'studyArticleDetail'}
                                        onAppFieldValidate={updateStudyValidation}
                                        onDelete={async (row) => {
                                            const deleteIndex = await handleStudyArticleDelete(row);
                                            handleLicenceDelete(licenceRows[deleteIndex]);
                                        }}
                                        onEdit={handleStudyArticleEditClick}
                                        onDisableChange={(isDisable) => {
                                            if (isInitialize) {
                                                if (isDisable) {
                                                    onStudyArticleDataEmpty();
                                                } else {
                                                    recoverStudyArticleDataCreation();
                                                }
                                            }
                                        }}
                                        applicationCreateDtm={applicationCreateDtm}
                                    />
                                </Grid>
                            </NumberListFormItem>
                            <NumberListFormItem
                                displayContentNextLine
                                number={'11.2.3'}
                                level={2}
                                label={'Study article licence registration'}
                                highlight={isHighlight(['studyArticleLicense'], true)}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.2.3 Study article licence registration', 
                                    commentStatus: null,
                                    commentField: 'StudyArticleLicense',
                                    commentDetail: getCommentDetail("StudyArticleLicense", allComments),
                                }}
                            >
                                <Grid container>
                                    <CustomTable
                                        disableTableBorder
                                        rows={licenceRows}
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        columns={licenceColumns}
                                        allowAction={getData('p2StudyArticle') === true}
                                        isEditAllow
                                        showGroupTab
                                        name={'StudyArticleLicense'}
                                        onAppFieldValidate={(name, value) => {
                                            // temp fix of sit log
                                            if (getData('p2StudyArticle') === false) {
                                                return updateStudyValidation(name, true);
                                            }

                                            let isValid = isLicenceTableValid(licenceRows);

                                            if (!isProspective() || getData('p2StudyArticle') === false) {
                                                isValid = true;
                                            }

                                            updateStudyValidation(name, value && isValid);
                                        }}
                                        disabled={!isProspective()}
                                        onDelete={handleLicenceDelete}
                                        onEdit={handleLicenceEditClick}
                                        onDisableChange={(isDisable) => {
                                            if (isInitialize) {
                                                if (isDisable) {
                                                    onLicenceDataEmpty();
                                                } else {
                                                    recoverLicenceDataCreation();
                                                }
                                            }
                                        }}
                                        applicationCreateDtm={applicationCreateDtm}
                                    />
                                </Grid>
                            </NumberListFormItem>
                            <NumberListFormItem
                                label={'Overseas Country should be blank when the answer of In Overseas is “No”'}
                                level={2}
                            />
                            <NumberListFormItem
                                number={'11.2.4'}
                                disableOnPermission={disableOnPermission}
                                level={2}
                                label={'Will a Certificate for Clinical Trial (“CTC”) be obtained for the Study? '}
                                labelTooltip={`Under Regulation 36B (the regulation) of the Pharmacy and Poisons Regulations, a
                                Certificate for Clinical Trial/Medicinal Test (the certificate) is required for the purpose of
                                conducting a clinical trial on human beings or a medicinal test on animals. The regulation only
                                applies to pharmaceutical products.
                                `}
                                showTrueFalse
                                updateApplicationFormOnDisableChange={formId}
                                trueFalseValue={getData('p2CertificateCTC')}
                                onAppFieldValidate={updateStudyValidation}
                                disableCheckBox={!isProspective()}
                                name={'p2CertificateCTC'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p2CertificateCTC'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.2.4 Will a Certificate for Clinical Trial (“CTC”) be obtained for the Study?', 
                                    commentField: 'p2CertificateCTC',
                                    commentDetail: getCommentDetail("p2CertificateCTC", allComments),
                                }}
                            />
                            <NumberListFormItem
                                number={'11.2.4.1'}
                                level={3}
                                highlight={isHighlight(['p2Justification'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.2.4.1 If no, justification', 
                                    commentField: 'p2Justification',
                                    commentDetail: getCommentDetail("p2Justification", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2Justification')}
                                    name={'p2Justification'}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    disabled={!isProspective()}
                                    allowAction={getData('p2CertificateCTC') === false}
                                    onChange={onFieldChange}
                                    label={'If no, justification'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.3'}
                                label={'Number of extra visits / admission on top of usual care'}
                                showDisableMessage
                                displayContentNextLine
                                highlight={isHighlight(['p2NumOfExtra'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.3 Number of extra visits / admission on top of usual care', 
                                    commentField: 'p2NumOfExtra',
                                    commentDetail: getCommentDetail("p2NumOfExtra", allComments),
                                }}
                            >
                                <CustomTextField
                                    style={{ width: 500 }}
                                    value={getData('p2NumOfExtra')}
                                    maxLength={255}
                                    updateApplicationFormOnDisableChange={formId}
                                    name={'p2NumOfExtra'}
                                    disableOnPermission
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.4'}
                                label={
                                    'Will any of the study interventions / procedures be performed by persons other than the investigators?'
                                }
                                showTrueFalse
                                displayContentNextLine
                                disableVerticalCenter
                                updateApplicationFormOnDisableChange={formId}
                                trueFalseValue={getData('p2OtherInvestigator')}
                                name={'p2OtherInvestigator'}
                                onTrueFalseSelected={onFieldChange}
                                showDisableMessage
                                disableOnPermission
                                highlight={isHighlight(['p2OtherInvestigator'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.4 Will any of the study interventions / procedures be performed by persons other than the investigators?', 
                                    commentField: 'p2OtherInvestigator',
                                    commentDetail: getCommentDetail("p2OtherInvestigator", allComments),
                                }}
                            />
                            <NumberListFormItem number={'11.4.1'} level={2} highlight={isHighlight(['p2WhomWhere'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.4.1 If yes, by whom and where', 
                                    commentField: 'p2WhomWhere',
                                    commentDetail: getCommentDetail("p2WhomWhere", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    label={'If yes, by whom and where'}
                                    maxLength={255}
                                    value={getData('p2WhomWhere')}
                                    updateApplicationFormOnDisableChange={formId}
                                    disableOnPermission
                                    name={'p2WhomWhere'}
                                    allowAction={getData('p2OtherInvestigator') === true}
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                disableOnPermission={disableOnPermission}
                                showTrueFalse
                                number={'11.5'}
                                label={'Will biological samples be stored for future use?'}
                                updateApplicationFormOnDisableChange={formId}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p2SampleFurtherUse')}
                                name={'p2SampleFurtherUse'}
                                disableCheckBox={!isProspective()}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p2SampleFurtherUse'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5 Will biological samples be stored for future use?', 
                                    commentField: 'p2SampleFurtherUse',
                                    commentDetail: getCommentDetail("p2SampleFurtherUse", allComments),
                                }}
                            />
                            <NumberListFormItem
                                number={'11.5.1'}
                                level={2}
                                highlight={isHighlight(['p2SamplePurpose'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.1 What is the purpose to store the sample?', 
                                    commentField: 'p2SamplePurpose',
                                    commentDetail: getCommentDetail("p2SamplePurpose", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2SamplePurpose')}
                                    name={'p2SamplePurpose'}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    updateApplicationFormOnDisableChange={formId}
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    allowAction={getData('p2SampleFurtherUse') === true}
                                    disabled={!isProspective()}
                                    label={'What is the purpose to store the sample?'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.5.2'}
                                level={2}
                                highlight={isHighlight(['p2SampleNature'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.2 State the nature of the sample', 
                                    commentField: 'p2SampleNature',
                                    commentDetail: getCommentDetail("p2SampleNature", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2SampleNature')}
                                    name={'p2SampleNature'}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    updateApplicationFormOnDisableChange={formId}
                                    allowAction={getData('p2SampleFurtherUse') === true}
                                    onAppFieldValidate={updateStudyValidation}
                                    disabled={!isProspective()}
                                    onChange={onFieldChange}
                                    label={'State the nature of the sample'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.5.3'}
                                level={2}
                                highlight={isHighlight(['p2SampleAnticipate'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.3 Anticipate duration of storage', 
                                    commentField: 'p2SampleAnticipate',
                                    commentDetail: getCommentDetail("p2SampleAnticipate", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2SampleAnticipate')}
                                    name={'p2SampleAnticipate'}
                                    maxLength={255}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    allowAction={getData('p2SampleFurtherUse') === true}
                                    disabled={!isProspective()}
                                    onChange={onFieldChange}
                                    label={'Anticipate duration of storage'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                disableOnPermission={disableOnPermission}
                                number={'11.5.4'}
                                level={2}
                                label={'Will the samples be sent / stored outside Hong Kong?'}
                                showTrueFalse
                                updateApplicationFormOnDisableChange={formId}
                                onAppFieldValidate={updateStudyValidation}
                                allowAction={getData('p2SampleFurtherUse') === true}
                                disableCheckBox={!isProspective()}
                                trueFalseValue={getData('p2SampleOutsideHK')}
                                name={'p2SampleOutsideHK'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p2SampleOutsideHK'])}
                                labelWidth={600}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.4 Will the samples be sent / stored outside Hong Kong?', 
                                    commentField: 'p2SampleOutsideHK',
                                    commentDetail: getCommentDetail("p2SampleOutsideHK", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'11.5.5'}
                                labelWidth={600}
                                level={2}
                                label={
                                    'Will consent for future specified usage of the biological samples be obtained from the subject?'
                                }
                                labelTooltip={
                                    'Obtaining consent from subjects for future use of the biological samples is compulsory.'
                                }
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                trueFalseValue={getData('p2SampleConsent')}
                                updateApplicationFormOnDisableChange={formId}
                                onAppFieldValidate={updateStudyValidation}
                                allowAction={getData('p2SampleFurtherUse') === true}
                                disableCheckBox={!isProspective()}
                                name={'p2SampleConsent'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p2SampleConsent'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.5 Will consent for future specified usage of the biological samples be obtained from the subject?', 
                                    commentField: 'p2SampleConsent',
                                    commentDetail: getCommentDetail("p2SampleConsent", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'11.5.5.1'}
                                level={3}
                                label={'When will the consent be obtained?'}
                                highlight={isHighlight(['p2SampleObtained'])}
                                labelWidth={220}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.5.1 When will the consent be obtained?', 
                                    commentField: 'p2SampleObtained',
                                    commentDetail: getCommentDetail("p2SampleObtained", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15, display: 'flex' }}>
                                    <CustomOutlineSelect
                                        name={'p2SampleObtained'}
                                        disableOnPermission={disableOnPermission}
                                        value={getData('p2SampleObtained')}
                                        onChange={onFieldChange}
                                        updateApplicationFormOnDisableChange={formId}
                                        onAppFieldValidate={updateStudyValidation}
                                        allowAction={
                                            getData('p2SampleFurtherUse') === true &&
                                            getData('p2SampleConsent') === true
                                        }
                                        disabled={!isProspective()}
                                        choices={[
                                            {
                                                key: 'D',
                                                display: 'During the recruitment',
                                            },
                                            {
                                                key: 'P',
                                                display: 'Prior to the specified future usage of the sample',
                                            },
                                        ]}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.5.5.2'}
                                level={3}
                                highlight={isHighlight(['p2SampleJustification'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.5.2 Justification for consent to be obtained prior to the specific future usage of the sample', 
                                    commentField: 'p2SampleJustification',
                                    commentDetail: getCommentDetail("p2SampleJustification", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p2SampleJustification')}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    name={'p2SampleJustification'}
                                    onChange={onFieldChange}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    allowAction={
                                        getData('p2SampleFurtherUse') === true &&
                                        getData('p2SampleConsent') === true &&
                                        getData('p2SampleObtained') === 'P'
                                    }
                                    disabled={!isProspective()}
                                    label={
                                        'Justification for consent to be obtained prior to the specific future usage of the sample'
                                    }
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.5.5.3'}
                                level={3}
                                label={'Where will the consent be stated?'}
                                labelWidth={220}
                                highlight={isHighlight(['p2SampleStated'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.5.3 Where will the consent be stated?', 
                                    commentField: 'p2SampleStated',
                                    commentDetail: getCommentDetail("p2SampleStated", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15, display: 'flex' }}>
                                    <CustomOutlineSelect
                                        name={'p2SampleStated'}
                                        value={getData('p2SampleStated')}
                                        disableOnPermission={disableOnPermission}
                                        onChange={onFieldChange}
                                        updateApplicationFormOnDisableChange={formId}
                                        onAppFieldValidate={updateStudyValidation}
                                        allowAction={
                                            getData('p2SampleFurtherUse') === true &&
                                            getData('p2SampleConsent') === true
                                        }
                                        disabled={!isProspective()}
                                        choices={[
                                            {
                                                key: 0,
                                                display: 'In the same consent form per that for recruitment',
                                            },
                                            {
                                                key: 1,
                                                display: 'In a separate consent form',
                                            },
                                        ]}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'11.5.5.4'}
                                level={3}
                                highlight={isHighlight(['p2NoConsentJustification'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '11.5.5.4 Justification for no consent to be obtained', 
                                    commentField: 'p2NoConsentJustification',
                                    commentDetail: getCommentDetail("p2NoConsentJustification", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p2NoConsentJustification')}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    name={'p2NoConsentJustification'}
                                    onAppFieldValidate={updateStudyValidation}
                                    updateApplicationFormOnDisableChange={formId}
                                    allowAction={
                                        getData('p2SampleFurtherUse') === true && getData('p2SampleConsent') === false
                                    }
                                    disabled={!isProspective()}
                                    onChange={onFieldChange}
                                    label={'Justification for no consent to be obtained'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                bold
                                number={'12'}
                                label={'Research Subject Protection'}
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />
                            <NumberListFormItem
                                number={'12.1'}
                                highlight={isHighlight(['p2Significant'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '12.1 Significant difference(s) from usual management', 
                                    commentField: 'p2Significant',
                                    commentDetail: getCommentDetail("p2Significant", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p2Significant')}
                                    name={'p2Significant'}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    disabled={!isProspective()}
                                    onChange={onFieldChange}
                                    label={'Significant difference(s) from usual management'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'12.2'}
                                highlight={isHighlight(['p2Benefits'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '12.2 Anticipated benefits to study subjects', 
                                    commentField: 'p2Benefits',
                                    commentDetail: getCommentDetail("p2Benefits", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p2Benefits')}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    name={'p2Benefits'}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    disabled={!isProspective()}
                                    onChange={onFieldChange}
                                    label={'Anticipated benefits to study subjects'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'12.3'}
                                displayContentNextLine
                                label={
                                    'Will the subjects be provided with a card indicating their participation in study and means of urgent contact?'
                                }
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                updateApplicationFormOnDisableChange={formId}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p2ProvidedCard')}
                                disableCheckBox={!isProspective()}
                                name={'p2ProvidedCard'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p2ProvidedCard'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '12.3 Will the subjects be provided with a card indicating their participation in study and means of urgent contact?', 
                                    commentField: 'p2ProvidedCard',
                                    commentDetail: getCommentDetail("p2ProvidedCard", allComments),
                                }}
                            />
                            <NumberListFormItem label={'If yes, please attach the card in “Other documents”'} italic />
                            <NumberListFormItem
                                number={'12.3.1'}
                                level={2}
                                highlight={isHighlight(['p2HowToIdentified'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '12.3.1 If no, state how the research subjects can be identified in case of emergency', 
                                    commentField: 'p2HowToIdentified',
                                    commentDetail: getCommentDetail("p2HowToIdentified", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p2HowToIdentified')}
                                    name={'p2HowToIdentified'}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    disableOnPermission={disableOnPermission}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    allowAction={getData('p2ProvidedCard') === false}
                                    disabled={!isProspective()}
                                    onChange={onFieldChange}
                                    label={
                                        'If no, state how the research subjects can be identified in case of emergency'
                                    }
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'12.4'}
                                displayContentNextLine
                                label={'Does the protocol state compliance with the ICH-GCP?'}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                updateApplicationFormOnDisableChange={formId}
                                trueFalseValue={getData('p2ICHGCP')}
                                disableCheckBox={!isProspective()}
                                name={'p2ICHGCP'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p2ICHGCP'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '12.4 Does the protocol state compliance with the ICH-GCP?', 
                                    commentField: 'p2ICHGCP',
                                    commentDetail: getCommentDetail("p2ICHGCP", allComments),
                                }}
                            />
                            <NumberListFormItem
                                number={'12.4.1'}
                                level={2}
                                highlight={isHighlight(['p2ICHGCPJustification'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '12.4.1 If no, justification', 
                                    commentField: 'p2ICHGCPJustification',
                                    commentDetail: getCommentDetail("p2ICHGCPJustification", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p2ICHGCPJustification')}
                                    name={'p2ICHGCPJustification'}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    allowAction={getData('p2ICHGCP') === false}
                                    disableOnPermission={disableOnPermission}
                                    onAppFieldValidate={updateStudyValidation}
                                    updateApplicationFormOnDisableChange={formId}
                                    disabled={!isProspective()}
                                    onChange={onFieldChange}
                                    label={'If no, justification'}
                                />
                            </NumberListFormItem>
                        </>
                    )}
                </SLayout>
            </Paper>
        </SPaperLayout>
    );
}
